import React, { ReactNode, useEffect, useMemo, useState } from 'react';

import { getFirstMissingGateAgreementType } from '../../../redux/api/agreements/agreementPolicy';
import { AgreementType } from '../../../redux/api/agreements/interfaces';
import { usePostAgreementAcceptance } from '../../../redux/api/agreementsApi';
import { useGetMyDetailsApiQuery } from '../../../redux/api/users/usersApi';
import {
  LocalStorageKey,
  LocalStorageService,
} from '../../../shared/storage/local-storage';
import AgreementsModal, { AgreementsModalMode } from './AgreementsModal';

interface IAgreementsGateProps {
  children: ReactNode;
}

const AgreementsGate = ({ children }: IAgreementsGateProps) => {
  const { data: me } = useGetMyDetailsApiQuery(undefined);
  const [postAgreementAcceptance, { isLoading: isAccepting }] =
    usePostAgreementAcceptance();
  const [pendingType, setPendingType] = useState<AgreementType | null>(null);

  const missingAgreementType = useMemo(
    () => getFirstMissingGateAgreementType(me),
    [me],
  );

  const currentAgreementType = pendingType ?? missingAgreementType;
  const isOpen = currentAgreementType !== null;

  useEffect(() => {
    const agreements = me?.agreements;
    if (!agreements) {
      return;
    }

    const isCookieAccepted = Boolean(agreements[AgreementType.COOKIE_POLICY]);

    if (isCookieAccepted) {
      LocalStorageService.setValue(LocalStorageKey.cookieConsentAccepted, true);
    }
  }, [me]);

  const handleAgree = async () => {
    if (!currentAgreementType) {
      return;
    }

    setPendingType(currentAgreementType);
    try {
      await postAgreementAcceptance({
        agreementTypes: [currentAgreementType],
      }).unwrap();
      if (currentAgreementType === AgreementType.COOKIE_POLICY) {
        LocalStorageService.setValue(
          LocalStorageKey.cookieConsentAccepted,
          true,
        );
      }
    } catch (error) {
      void error;
    } finally {
      setPendingType(null);
    }
  };

  return (
    <>
      {children}
      {currentAgreementType && (
        <AgreementsModal
          open={isOpen}
          mode={AgreementsModalMode.FORCED}
          agreementType={currentAgreementType}
          compactCookieForcedMode={false}
          onAgree={handleAgree}
          onClose={() => {}}
          isSubmitting={isAccepting}
        />
      )}
    </>
  );
};

export default AgreementsGate;
